import { ROUTES } from '@/routes/routes'

/**
 * The Transactions page keeps every filter in its URL, under the same names the list API uses,
 * so any page can open it pre-filtered with a link built here and the page passes the params
 * straight through.
 */

export const TRANSACTION_TYPES = [
  'EXPENSE',
  'INCOME',
  'TRANSFER',
  'REFUND',
  'PERSON_PAYMENT',
] as const
export type TransactionType = (typeof TRANSACTION_TYPES)[number]

export interface TransactionLinkFilters {
  /** "For spending in" months, yyyy-MM: INCOME by the month it covers. Wins over every date period. */
  spendMonthFrom?: string
  spendMonthTo?: string
  month?: string
  date?: string
  dateFrom?: string
  dateTo?: string
  type?: TransactionType
  financialAccountId?: string
  categoryId?: string
  attributedToHouseholdMemberId?: string
  contactId?: string
  goalId?: string
  isCapitalEvent?: boolean
  /** Only false is ever linked to: what's left to tick off. */
  reconciled?: false
  q?: string
}

const ID_KEYS = [
  'financialAccountId',
  'categoryId',
  'attributedToHouseholdMemberId',
  'contactId',
  'goalId',
] as const

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/** "All time" is an open-ended range from here — the API reads a lone dateFrom as "since". */
export const ALL_TIME_FROM = '2000-01-01'
export const ALL_TIME: TransactionLinkFilters = { dateFrom: ALL_TIME_FROM }

export const SEARCH_MIN = 2
export const SEARCH_MAX = 100

export function transactionsLink(
  filters: TransactionLinkFilters,
  opts: { back?: string | null } = {}
): string {
  const query = toSearchParams(filters, opts.back).toString()
  return query ? `${ROUTES.APP.TRANSACTIONS}?${query}` : ROUTES.APP.TRANSACTIONS
}

export function toSearchParams(
  filters: TransactionLinkFilters,
  back?: string | null
): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.spendMonthFrom) params.set('spendMonthFrom', filters.spendMonthFrom)
  if (filters.spendMonthTo) params.set('spendMonthTo', filters.spendMonthTo)
  if (filters.month) params.set('month', filters.month)
  if (filters.date) params.set('date', filters.date)
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  if (filters.type) params.set('type', filters.type)
  for (const key of ID_KEYS) {
    const value = filters[key]
    if (value) params.set(key, value)
  }
  if (filters.isCapitalEvent) params.set('isCapitalEvent', 'true')
  if (filters.reconciled === false) params.set('reconciled', 'false')
  if (filters.q) params.set('q', filters.q)
  const safeBack = safeBackPath(back ?? null)
  if (safeBack) params.set('back', safeBack)
  return params
}

/** Malformed values are dropped, never fatal: a stale or hand-edited link still opens the page. */
export function parseTransactionParams(params: URLSearchParams): TransactionLinkFilters {
  const filters: TransactionLinkFilters = {}
  const dateFrom = params.get('dateFrom')
  const dateTo = params.get('dateTo')
  const date = params.get('date')
  const month = params.get('month')
  const spendFrom = params.get('spendMonthFrom')
  const spendTo = params.get('spendMonthTo')
  // The API's precedence, so the URL never says two different things.
  const validSpendFrom = spendFrom && MONTH_RE.test(spendFrom) ? spendFrom : null
  const validSpendTo = spendTo && MONTH_RE.test(spendTo) ? spendTo : null
  const validFrom = dateFrom && DATE_RE.test(dateFrom) ? dateFrom : null
  const validTo = dateTo && DATE_RE.test(dateTo) ? dateTo : null
  if (validSpendFrom || validSpendTo) {
    filters.spendMonthFrom = validSpendFrom ?? validSpendTo ?? undefined
    filters.spendMonthTo = validSpendTo ?? validSpendFrom ?? undefined
  } else if (validFrom || validTo) {
    if (validFrom) filters.dateFrom = validFrom
    if (validTo) filters.dateTo = validTo
  } else if (date && DATE_RE.test(date)) {
    filters.date = date
  } else if (month && MONTH_RE.test(month)) {
    filters.month = month
  }

  const type = params.get('type')
  if (type && (TRANSACTION_TYPES as readonly string[]).includes(type)) {
    filters.type = type as TransactionType
  }
  for (const key of ID_KEYS) {
    const value = params.get(key)
    if (value && UUID_RE.test(value)) filters[key] = value
  }
  if (params.get('isCapitalEvent') === 'true') filters.isCapitalEvent = true
  if (params.get('reconciled') === 'false') filters.reconciled = false
  const q = params.get('q')?.trim()
  if (q && q.length >= SEARCH_MIN && q.length <= SEARCH_MAX) filters.q = q
  return filters
}

/** Only in-app paths: `back` comes from the URL, so it must never send anyone off-site. */
export function safeBackPath(raw: string | null): string | null {
  if (!raw || !raw.startsWith('/app/') || raw.includes('\\')) return null
  return raw
}

const BACK_LABELS: [prefix: string, label: string][] = [
  [`${ROUTES.APP.ACCOUNTS}/`, 'Back to account'],
  [`${ROUTES.APP.LOANS}/`, 'Back to loan'],
  [`${ROUTES.APP.PEOPLE}/`, 'Back to person'],
  [`${ROUTES.APP.GOALS}/`, 'Back to goal'],
  [`${ROUTES.APP.ANALYTICS}/`, 'Back to Analytics'],
  [ROUTES.APP.DASHBOARD, 'Back to Dashboard'],
  [ROUTES.APP.CALENDAR, 'Back to Calendar'],
  [ROUTES.APP.BUDGET, 'Back to Budget'],
  [ROUTES.APP.ANALYTICS, 'Back to Analytics'],
  [ROUTES.APP.ACCOUNTS, 'Back to Accounts'],
  [ROUTES.APP.LOANS, 'Back to Loans'],
  [ROUTES.APP.PEOPLE, 'Back to People'],
  [ROUTES.APP.GOALS, 'Back to Goals'],
  [ROUTES.APP.RECURRING, 'Back to Recurring'],
]

/** The label comes from our own routes, never from the URL. */
export function backLabel(path: string): string {
  const pathname = path.split('?')[0]
  for (const [prefix, label] of BACK_LABELS) {
    if (prefix.endsWith('/') ? pathname.startsWith(prefix) : pathname === prefix) return label
  }
  return 'Back'
}

/** The current location as a `back` value. */
export function currentPath(location: { pathname: string; search: string }): string {
  return `${location.pathname}${location.search}`
}
